<template>
  <div class="mt-3">
    <!-- ----------------------------------------------------- -->
    <!-- main headings -->
    <!-- ----------------------------------------------------- -->
    <div class="mt-3">
      <div class="row">
        <p class="bb-sheet-title">CHARACTERISTIC SKILL FORMULA ADJS</p>
      </div>
    </div>
    <div
      v-if="isReady"
      class="container-fluid mt-3 mb-0 ms-3"
    >
      <!-- ----------------------------------------------------- -->
      <!-- table headings -->
      <!-- ----------------------------------------------------- -->
      <div class="row">
        <div class="col-1 text-center">
          <p class="bb-hdg mt-2 mb-3">Characteristic</p>
        </div>
        <div class="col-1 text-center">
          <p class="bb-hdg mt-2 mb-3">Adjs</p>
        </div>
        <div class="col-3 text-center">
          <p class="bb-hdg mt-2 mb-3">Reasons</p>
        </div>
        <div class="col-auto ms-3">
          <p class="bb-hdg mt-2 mb-3">&nbsp;</p>
        </div>
      </div>
      <!-- ----------------------------------------------------- -->
      <!-- Data Rows -->
      <!-- ----------------------------------------------------- -->
      <div
        :class="[
          'row mt-3 align-items-center bb-tooltip-wrapper',
          char === 'cha' ? 'mb-0' : 'mb-3',
        ]"
        v-for="(charLabel, char) in charLabels"
        :key="char"
      >
        <!-- ----------------------------------------------------- -->
        <!-- Characteristic -->
        <!-- ----------------------------------------------------- -->
        <div class="col-1 text-center">
          <div class="form-group">
            <div class="form-floating">
              <input
                readonly
                class="form-control text-center"
                :value="charLabel"
                :id="getCharId(char)"
                disabled
              />
              <label
                class="readonly bb-float-label optional"
                :for="getCharId(char)"
              >
                {{ charLabel }}
              </label>
            </div>
          </div>
        </div>
        <!-- ----------------------------------------------------- -->
        <!-- Adj -->
        <!-- ----------------------------------------------------- -->
        <div class="col-1 text-center">
          <div class="form-group">
            <div class="form-floating">
              <input
                readonly
                class="form-control text-center"
                :value="adjsTotDisplay(char)"
                :id="`${getCharId(char)}_adjs_tot`"
                disabled
              />
              <label
                class="readonly bb-float-label optional"
                :for="`${getCharId(char)}_adjs_tot`"
              >
                {{ charLabel }}
              </label>
            </div>
          </div>
        </div>
        <!-- ----------------------------------------------------- -->
        <!-- Adjustment Reason (tooltip on every row) -->
        <!-- ----------------------------------------------------- -->
        <div class="col-3 text-center">
          <ToolTip
            :text="displayAdjReasonsText(char)"
            position="top"
            style="display: block"
          >
            <div class="form-group">
              <div class="form-floating">
                <input
                  readonly
                  class="form-control"
                  :value="displayAdjReasonsText(char)"
                  :id="`${getCharId(char)}_adjs_reasons`"
                  disabled
                />
                <label
                  class="readonly bb-float-label optional"
                  :for="`${getCharId(char)}_adjs_reasons`"
                >
                  All {{ charLabel }} Skill Adj Reasons
                  <span
                    v-if="computedReasons(char)"
                    class="bb-orange-circle"
                  ></span>
                </label>
              </div>
            </div>
          </ToolTip>
        </div>
        <!-- ----------------------------------------------------- -->
        <!-- Adjustment Button -->
        <!-- ----------------------------------------------------- -->
        <div class="col-auto ms-2">
          <button
            :id="getCharId(char)"
            type="button"
            class="btn btn-primary btn-xl btn-circle d-flex justify-content-center align-items-center"
            @click="goOpenAdjs(char)"
          >
            <ToolTip
              :text="`${charLabel} Adjustments`"
              position="left"
            >
              <img
                :src="adjustImg"
                height="48"
                width="48"
                alt="adjustments image"
              />
            </ToolTip>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ToolTip from '../components/ToolTip.vue'

import adjustImg from '../assets/images/bbAdjust.png'

import copyObjectNoRef from '../helpers/copyObjectNoRef'
import getEffectiveCharacteristicSkillAdjs from '../helpers/getEffectiveCharacteristicSkillAdjs'

import { inject } from 'vue'

export default {
  name: 'CharacterSheetCharacteristicSkillAdjs',
  //========================================
  // props
  //-------
  props: {
    character: {
      type: Object,
      required: true,
    },
  },
  //========================================================
  // components
  //------------
  components: {
    ToolTip,
  },
  //========================================================
  // setup
  //-------
  setup() {
    const global = inject('global')
    return { global }
  },
  //========================================================
  // watch
  //-------
  watch: {
    //-----------------
    // watch character
    //-----------------
    character: {
      handler(newChar) {
        if (!newChar) return
        // reload local_character for new/updated character
        this.local_character = copyObjectNoRef(this.character)
      },
      deep: true,
      immediate: true,
    },
  },
  //========================================================
  // data
  //------
  data() {
    return {
      adjustImg,
      local_character: null,
      isInitialLoad: true,
      charLabels: {
        str: 'Str',
        con: 'Con',
        dex: 'Dex',
        siz: 'Siz',
        int: 'Int',
        pow: 'Pow',
        cha: 'Cha',
      },
    }
  },
  //========================================================
  // computed
  //-----------
  computed: {
    isReady() {
      if (this.local_character != null) {
        return true
      }
      return false
    },
    characteristicSkillAdjs() {
      return getEffectiveCharacteristicSkillAdjs(this.character)
    },
  },
  //========================================================
  // methods
  //---------
  methods: {
    adjsTotDisplay(char) {
      const val = this.characteristicSkillAdjs
        .filter((adj) => (adj.name || adj.characteristic_name) === char)
        .reduce((sum, adj) => sum + (adj.adj_value || 0), 0)

      return val > 0 ? `+${val}` : `${val}`
    },
    displayAdjReasonsText(char) {
      if (!char || typeof char !== 'string') return ''

      const reasons = this.characteristicSkillAdjs
        .filter((adj) => (adj.name || adj.characteristic_name) === char)
        .map((adj) => {
          const value = adj.adj_value || 0
          const prefix = value > 0 ? `+${value}` : `${value}`
          return `${adj.adj_reason} (${prefix})`
        })
        .filter((reason) => reason.trim() !== '')

      if (reasons.length > 0) {
        return reasons.join('. ')
      }

      return ''
    },
    computedReasons(char) {
      return this.characteristicSkillAdjs.some(
        (adj) =>
          (adj.name || adj.characteristic_name) === char &&
          `${adj.adj_reason || ''}`.trim(),
      )
    },
    getTextSizeClass(text) {
      if (!text) return 'text-normal'

      const length = text.length
      if (length > 100) return 'text-xs'
      if (length > 70) return 'text-sm'
      if (length > 40) return 'text-md'
      return 'text-normal'
    },
    getCharId(char) {
      return (char || '').toLowerCase()
    },
    goOpenAdjs(char) {
      console.log('>>>> emitting adjust for char ', char)
      this.$emit('adjust', {
        adj_category: 'characteristic-skill',
        adj_type: char,
      })
    },
  },
  //========================================================
  // mounted
  //---------
  mounted() {},
}
</script>

<style scoped>
.form-group {
  margin-bottom: 0;
}
</style>
